import { supabase } from "@/integrations/supabase/client";
import { routePattern } from "@/hooks/use-track-page-view";

/**
 * Versões do front no mesmo domínio. A borda (CloudFront Function
 * corisco-app-prod-url-rewrite) serve /novo/index.html quando o cookie
 * corisco_versao=nova, /v3/index.html quando v3, e o /index.html de sempre nos outros
 * casos. Mesmas URLs, mesma sessão: trocar é gravar o cookie e recarregar a página.
 */
export type Versao = "antiga" | "nova" | "v3";

export const VERSAO_ATUAL: Versao = "antiga";

const COOKIE = "corisco_versao";
/** Lido uma vez pela versão nova, que mostra o aviso de que a equipe levou a pessoa para lá. */
const COOKIE_AVISO = "corisco_versao_reconduzida";

/** Pasta de cada versão no bucket (a antiga é a raiz). */
const PASTA: Record<Versao, string> = { antiga: "", nova: "/novo", v3: "/v3" };

function ehVersao(valor: string | null): valor is Versao {
  return valor === "antiga" || valor === "nova" || valor === "v3";
}
const UM_ANO = 60 * 60 * 24 * 365;

function gravarCookie(versao: Versao) {
  const seguro = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${COOKIE}=${versao}; Path=/; Max-Age=${UM_ANO}; SameSite=Lax${seguro}`;
}

/** Versão para a qual a equipe levou a pessoa de volta (versao_trocas.pela_equipe), se ainda vale. */
export async function versaoReconduzida(): Promise<Versao | null> {
  // versao_reconduzida ainda não está no types.ts gerado.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await supabase.rpc("versao_reconduzida" as any);
  if (error) return null;
  const versao = data as unknown as string | null;
  return ehVersao(versao) ? versao : null;
}

/** Vai para a versão em que a equipe colocou a pessoa, deixando o aviso para a outra versão mostrar. */
export function seguirVersaoReconduzida(para: Versao) {
  const seguro = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${COOKIE_AVISO}=1; Path=/; Max-Age=600; SameSite=Lax${seguro}`;
  gravarCookie(para);
  window.location.reload();
}

/** Registra a troca (motivo opcional) e recarrega a mesma página na outra versão. */
export async function trocarVersao(para: Versao, motivo?: string) {
  const registro = supabase
    // registrar_troca_versao ainda não está no types.ts gerado.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .rpc("registrar_troca_versao" as any, {
      p_de: VERSAO_ATUAL,
      p_para: para,
      p_motivo: motivo?.trim() || null,
      p_rota: routePattern(window.location.pathname),
    } as never)
    .then(() => undefined, () => undefined);
  // Não segura a troca se o registro demorar.
  await Promise.race([registro, new Promise((resolve) => setTimeout(resolve, 1500))]);
  gravarCookie(para);
  window.location.reload();
}

/** Link de teste (?versao=antiga|nova|v3): a borda já serviu a versão pedida; aqui só fica gravada. */
export function aplicarVersaoDaUrl() {
  const params = new URLSearchParams(window.location.search);
  const pedida = params.get("versao");
  if (!ehVersao(pedida)) return;
  gravarCookie(pedida);
  params.delete("versao");
  const query = params.toString();
  window.history.replaceState(window.history.state, "", `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`);
}

/**
 * A versão está publicada? Sem ela, o CloudFront devolve o index antigo (erro 403/404
 * vira /index.html com 200), então o teste é o conteúdo, não o status.
 */
export async function versaoDisponivel(versao: Versao): Promise<boolean> {
  const pasta = PASTA[versao];
  if (!pasta) return true;
  try {
    const resposta = await fetch(`${pasta}/index.html`, { cache: "no-store" });
    return resposta.ok && (await resposta.text()).includes(`${pasta}/assets/`);
  } catch {
    return false;
  }
}
